/**
 * LiveSignalFeed (Plan v9 §4.2 prototype rework).
 *
 * Renders a reverse-timeline list of recent learning signals plus a
 * header that surfaces the total events-indexed count. Mirrors the
 * prototype's "Live 847 events indexed" banner.
 *
 * Data source: `MetricsSnapshot.liveSignals` + `eventsIndexedCount`.
 * Demo Mode supplies a deterministic seed (HC1 byte-identical); Live
 * Mode populates these fields from App Insights queries.
 *
 * HC2 token-only: every color, spacing, radius value resolves through
 * Signal DS tokens via Tailwind. No raw hex.
 * HC5: zero em / en dashes anywhere. Hyphens only.
 * HC6 a11y: the feed is `role="log"` `aria-live="polite"` so SR users
 *   are notified when new entries land. The header carries an
 *   `aria-label` summarizing the total events indexed. The "Live"
 *   pulse dot is `aria-hidden="true"`.
 */
import type { LiveSignalEntry, LiveSignalKind } from '../../services/metrics/types';

/** Display label per kind. Closed vocabulary so the SR labels are stable. */
const KIND_LABEL: Record<LiveSignalKind, string> = {
  encounter_indexed: 'Encounter indexed',
  nba_approved: 'NBA Approved',
  override_captured: 'Override captured',
  prom_signal_ingested: 'PROM signal ingested',
};

/** Display glyph (text-only) per kind. Aria-hidden in render. */
const KIND_GLYPH: Record<LiveSignalKind, string> = {
  encounter_indexed: 'ENC',
  nba_approved: 'NBA',
  override_captured: 'OVR',
  prom_signal_ingested: 'PRM',
};

export interface LiveSignalFeedProps {
  /** Reverse-timeline list of signals (newest first). */
  signals: ReadonlyArray<LiveSignalEntry>;
  /** Total events indexed in the search index. Surfaced in the header. */
  eventsIndexedCount: number;
  /** Optional className for the outer wrapper. */
  className?: string;
  /**
   * Maximum entries to render. Defaults to 8 to match the prototype's
   * compact feel; the snapshot list may be longer.
   */
  maxEntries?: number;
}

/**
 * Format a count with locale-aware grouping (e.g. `847`, `1,234`).
 */
function formatCount(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
}

/**
 * Format an ISO timestamp as a short HH:MM:SS UTC label. Avoids the
 * locale's day-of-week so the visual is compact and the snapshot is
 * deterministic across CI runners (no env-dependent timezone offset).
 */
function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} UTC`;
}

export function LiveSignalFeed({
  signals,
  eventsIndexedCount,
  className,
  maxEntries = 8,
}: LiveSignalFeedProps) {
  const visible = signals.slice(0, maxEntries);
  const composed = [
    'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      aria-labelledby="live-signal-feed-title"
      data-testid="live-signal-feed"
      data-events-indexed={eventsIndexedCount}
      className={composed}
    >
      <header className="flex flex-wrap items-start justify-between gap-gutter-md">
        <div className="flex flex-col gap-gutter-sm">
          <h3
            id="live-signal-feed-title"
            className="text-signal-lg font-semibold text-text-primary"
          >
            Live Learning Signals
          </h3>
          <p className="text-signal-sm text-text-secondary">
            Clinician decisions captured in real time. Reverse-timeline,
            newest first.
          </p>
        </div>
        <div
          data-testid="live-signal-feed-header-badge"
          aria-label={`Live. ${formatCount(eventsIndexedCount)} events indexed in search index.`}
          className="inline-flex items-center gap-gutter-sm rounded-signal-pill border border-status-active bg-status-active-bg px-gutter-md py-1 text-signal-xs font-semibold text-status-active-fg"
        >
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 rounded-signal-pill bg-status-active"
          />
          <span>Live</span>
          <span
            data-testid="live-signal-feed-events-indexed"
            className="font-mono text-signal-xs text-status-active-fg"
          >
            {formatCount(eventsIndexedCount)} events indexed
          </span>
        </div>
      </header>

      {visible.length === 0 ? (
        <p
          data-testid="live-signal-feed-empty"
          className="rounded-signal-md border border-dashed border-surface-border bg-surface-muted px-gutter-md py-gutter-lg text-signal-sm text-text-secondary"
        >
          No live signals yet. Decisions will appear here as clinicians
          interact with the pathway.
        </p>
      ) : (
        <ol
          role="log"
          aria-live="polite"
          aria-label="Live learning signal feed"
          data-testid="live-signal-feed-list"
          className="flex flex-col gap-gutter-sm"
        >
          {visible.map((entry) => (
            <li
              key={entry.id}
              data-testid={`live-signal-${entry.id}`}
              data-kind={entry.kind}
              className="flex items-start gap-gutter-md rounded-signal-md border border-surface-border bg-surface-muted px-gutter-md py-gutter-sm"
            >
              <span
                aria-hidden="true"
                className="mt-1 inline-flex h-7 min-w-7 items-center justify-center rounded-signal-pill bg-signal-primary-100 px-2 font-mono text-signal-xs font-semibold text-signal-primary-700"
              >
                {KIND_GLYPH[entry.kind]}
              </span>
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-baseline justify-between gap-gutter-sm">
                  <span className="text-signal-xs font-semibold uppercase tracking-wide text-text-tertiary">
                    {KIND_LABEL[entry.kind]}
                  </span>
                  <span
                    data-testid={`live-signal-${entry.id}-ts`}
                    className="font-mono text-signal-xs text-text-tertiary"
                  >
                    {formatTime(entry.ts)}
                  </span>
                </div>
                <p className="text-signal-sm text-text-primary">
                  {entry.summary}
                </p>
                {entry.detail ? (
                  <p className="text-signal-xs text-text-secondary">
                    {entry.detail}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
